/**
 * Pinia store for Explorer Mode state management
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { DomainType, Section, DomainData } from '@/types/ashmatics';
import { useExplorerData } from '@/composables/useExplorerData';
import { api } from '@/config/api';

export const useExplorerStore = defineStore('explorer', () => {
  // State - Default to Framework domain as the entry point
  const activeDomain = ref<DomainType>('framework');
  const activeSection = ref<Section | null>(null);
  const policyDomainData = ref<DomainData | null>(null);
  const processDomainData = ref<DomainData | null>(null);
  const frameworkDomainData = ref<DomainData | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Navigation history tracking
  const navigationHistory = ref<{ domain: DomainType; section: Section }[]>([]);
  const lastVisitedByDomain = ref<Record<DomainType, Section | null>>({
    framework: null,
    policy: null,
    process: null,
  });

  // Related-artifact peek modal. Lets a "Related Processes" / "Related Policies"
  // card (or a SOPs-tab card) open the referenced artifact in place, without
  // changing the user's current sidebar navigation context.
  //
  // Two modes:
  // - kind 'section': an already-loaded domain item (policy/process/framework
  //   overview). `section` is null when the reference can't be resolved (e.g. a
  //   renumbered/orphaned id) so the modal can show a graceful "not available".
  // - kind 'sop': a KB artifact (SOP) that is NOT in the loaded domain data, so
  //   it is fetched lazily on open; `loading`/`fetched` track that fetch.
  const relatedArtifact = ref<{
    visible: boolean;
    kind: 'section' | 'sop';
    domain: DomainType;
    id: string;
    section: Section | null;
    loading: boolean;
    fetched: { title: string; markdown: string } | null;
  }>({ visible: false, kind: 'section', domain: 'policy', id: '', section: null, loading: false, fetched: null });

  // Composables
  const { fetchPolicyDomain, fetchProcessDomain, fetchFrameworkDomain } = useExplorerData();

  // Computed
  const currentDomainData = computed(() => {
    switch (activeDomain.value) {
      case 'policy':
        return policyDomainData.value;
      case 'process':
        return processDomainData.value;
      case 'framework':
        return frameworkDomainData.value;
      default:
        return null;
    }
  });

  const currentDomainSections = computed(() => {
    return currentDomainData.value?.sections || [];
  });

  // Breadcrumb navigation path
  const breadcrumbs = computed(() => {
    const crumbs: { label: string; id?: string }[] = [];

    // Add domain
    const domainLabels: Record<DomainType, string> = {
      framework: 'Framework',
      policy: 'Policy Domain',
      process: 'Process Domain',
    };
    crumbs.push({ label: domainLabels[activeDomain.value] });

    // Add active section hierarchy
    if (activeSection.value) {
      // Find parent section if any
      const allSections = currentDomainSections.value;
      for (const section of allSections) {
        if (section.subsections) {
          for (const subsection of section.subsections) {
            if (subsection.id === activeSection.value.id) {
              // Add parent section
              crumbs.push({ label: section.title, id: section.id });
              break;
            }
          }
        }
      }
      // Add current section
      crumbs.push({ label: activeSection.value.title, id: activeSection.value.id });
    }

    return crumbs;
  });

  // Actions
  const switchDomain = async (domain: DomainType) => {
    activeDomain.value = domain;

    // Check if we have a last-visited section for this domain
    const lastVisited = lastVisitedByDomain.value[domain];

    if (lastVisited) {
      // Restore last visited section
      activeSection.value = lastVisited;
    } else {
      // For all domains, ensure data is loaded then default to overview section (0.1)
      if (domain === 'framework') {
        await loadFrameworkDomain();
      } else if (domain === 'policy') {
        await loadPolicyDomain();
      } else if (domain === 'process') {
        await loadProcessDomain();
      }

      // Get the appropriate domain data
      const domainData = domain === 'framework' ? frameworkDomainData.value
                       : domain === 'policy' ? policyDomainData.value
                       : processDomainData.value;

      // Search for section 0.1 in the loaded data
      if (domainData?.sections) {
        let overviewSection: Section | null = null;

        for (const section of domainData.sections) {
          if (section.subsections) {
            for (const subsection of section.subsections) {
              if (subsection.id === '0.1') {
                overviewSection = subsection;
                break;
              }
            }
          }
          if (overviewSection) break;
        }

        if (overviewSection) {
          activeSection.value = overviewSection;
          lastVisitedByDomain.value[domain] = overviewSection;
        } else {
          activeSection.value = null;
        }
      }
    }
  };

  const setActiveSection = (section: Section | null) => {
    if (section) {
      // Track in navigation history
      navigationHistory.value.push({
        domain: activeDomain.value,
        section,
      });

      // Keep history to last 50 items
      if (navigationHistory.value.length > 50) {
        navigationHistory.value.shift();
      }

      // Update last visited for current domain
      lastVisitedByDomain.value[activeDomain.value] = section;
    }

    activeSection.value = section;
  };

  // Helper to find section by ID
  const findSectionById = (id: string): Section | null => {
    const sections = currentDomainSections.value;
    for (const section of sections) {
      if (section.subsections) {
        for (const subsection of section.subsections) {
          if (subsection.id === id) {
            return subsection;
          }
        }
      }
    }
    return null;
  };

  // Look up an item by id within a SPECIFIC domain's data (not just the active
  // one). Used for cross-domain "related artifact" references, e.g. a policy
  // card linking to a process domain. All three domains are loaded by
  // initialize(), so this is a synchronous in-memory lookup.
  const findSectionInDomain = (domain: DomainType, id: string): Section | null => {
    const data = domain === 'policy' ? policyDomainData.value
               : domain === 'process' ? processDomainData.value
               : frameworkDomainData.value;
    if (!data?.sections) return null;
    for (const section of data.sections) {
      for (const subsection of section.subsections || []) {
        if (subsection.id === id) return subsection;
      }
    }
    return null;
  };

  // Open the related-artifact peek modal for (domain, id). Defensively ensures
  // the target domain is loaded (initialize() normally has), then resolves the
  // referenced item. A null section is expected for orphaned/renumbered ids and
  // is handled by the modal.
  const openRelatedArtifact = async (domain: DomainType, id: string) => {
    if (!id || id === 'all') return; // 'all' is handled by the tab's info card
    if (domain === 'policy') await loadPolicyDomain();
    else if (domain === 'process') await loadProcessDomain();
    else await loadFrameworkDomain();

    const dataLoaded =
      domain === 'policy'
        ? !!policyDomainData.value
        : domain === 'process'
          ? !!processDomainData.value
          : !!frameworkDomainData.value;
    if (!dataLoaded) return;

    relatedArtifact.value = {
      visible: true,
      kind: 'section',
      domain,
      id,
      section: findSectionInDomain(domain, id),
      loading: false,
      fetched: null,
    };
  };

  // Open a SOP (or any KB artifact) by slug. Unlike domain items, SOPs are not
  // in the loaded domain data, so the content is fetched lazily from the KB.
  const openSopArtifact = async (slug: string) => {
    if (!slug) return;
    relatedArtifact.value = {
      visible: true,
      kind: 'sop',
      domain: 'process',
      id: slug,
      section: null,
      loading: true,
      fetched: null,
    };
    try {
      const response = await api.get(`/explore/artifact/${encodeURIComponent(slug)}`);
      const data = response.data || {};
      // Race guard: a faster click may have replaced what's showing; only apply
      // this result if its slug is still the one in the (still-open) modal.
      if (relatedArtifact.value.visible && relatedArtifact.value.id === slug) {
        relatedArtifact.value.fetched = {
          title: data.title || slug,
          markdown: data.markdownContent || data.content || '',
        };
        relatedArtifact.value.loading = false;
      }
    } catch (err) {
      console.error(`[ExplorerStore] openSopArtifact(${slug}) failed:`, err);
      if (relatedArtifact.value.visible && relatedArtifact.value.id === slug) {
        relatedArtifact.value.loading = false;
        relatedArtifact.value.fetched = null; // -> modal shows "not available"
      }
    }
  };

  const closeRelatedArtifact = () => {
    relatedArtifact.value = { ...relatedArtifact.value, visible: false };
  };

  // Escape hatch from the modal: actually navigate to the artifact in its own
  // domain (switches the sidebar context and selects the section).
  const navigateToArtifact = (domain: DomainType, section: Section) => {
    activeDomain.value = domain;
    setActiveSection(section);
    closeRelatedArtifact();
  };

  const loadPolicyDomain = async () => {
    if (policyDomainData.value) return; // Already loaded

    isLoading.value = true;
    error.value = null;

    try {
      policyDomainData.value = await fetchPolicyDomain();
    } catch (err) {
      error.value = 'Failed to load policy domain data';
      console.error('Error loading policy domain:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const loadProcessDomain = async () => {
    if (processDomainData.value) return; // Already loaded

    isLoading.value = true;
    error.value = null;

    try {
      processDomainData.value = await fetchProcessDomain();
    } catch (err) {
      error.value = 'Failed to load process domain data';
      console.error('Error loading process domain:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const loadFrameworkDomain = async () => {
    if (frameworkDomainData.value) return; // Already loaded

    isLoading.value = true;
    error.value = null;

    try {
      frameworkDomainData.value = await fetchFrameworkDomain();
    } catch (err) {
      error.value = 'Failed to load framework domain data';
      console.error('Error loading framework domain:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const initialize = async () => {
    // Load all domains on initialization
    await Promise.all([loadPolicyDomain(), loadProcessDomain(), loadFrameworkDomain()]);

    // Set default Framework overview section (0.1) on app startup
    if (activeDomain.value === 'framework' && !activeSection.value) {
      console.log('[ExplorerStore] Initializing with Framework domain, looking for section 0.1');
      console.log('[ExplorerStore] Framework data loaded:', !!frameworkDomainData.value);
      console.log('[ExplorerStore] Framework sections:', frameworkDomainData.value?.sections?.length);

      const overviewSection = findSectionById('0.1');
      console.log('[ExplorerStore] Found overview section:', overviewSection);

      if (overviewSection) {
        activeSection.value = overviewSection;
        lastVisitedByDomain.value.framework = overviewSection;
        console.log('[ExplorerStore] Set active section to:', overviewSection.title);
      } else {
        console.warn('[ExplorerStore] Could not find section 0.1');
      }
    }
  };

  return {
    // State
    activeDomain,
    activeSection,
    policyDomainData,
    processDomainData,
    frameworkDomainData,
    isLoading,
    error,
    navigationHistory,
    lastVisitedByDomain,
    relatedArtifact,
    // Computed
    currentDomainData,
    currentDomainSections,
    breadcrumbs,
    // Actions
    switchDomain,
    setActiveSection,
    findSectionById,
    findSectionInDomain,
    openRelatedArtifact,
    openSopArtifact,
    closeRelatedArtifact,
    navigateToArtifact,
    loadPolicyDomain,
    loadProcessDomain,
    loadFrameworkDomain,
    initialize,
  };
});
